import { useState, useEffect } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/form/PasswordInput";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, LogOut } from "lucide-react";

function GateShell({ icon: Icon, title, subtitle, children, onLogout }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4" data-testid="forced-setup">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-2 text-sky-600"><Icon className="h-5 w-5" /><span className="text-[11px] font-bold uppercase tracking-wider">Action required</span></div>
        <h1 className="mt-2 font-heading text-xl font-bold text-slate-900">{title}</h1>
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        <div className="mt-5">{children}</div>
        <button onClick={onLogout} className="mt-4 flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-slate-600" data-testid="forced-setup-logout"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
      </div>
    </div>
  );
}

function ForcePassword({ onDone, onLogout }) {
  const [pw, setPw] = useState("");
  const [c, setC] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 15) return toast.error("Password must be at least 15 characters");
    if (pw !== c) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await client.post("/auth/force-password", { password: pw });
      toast.success("Password updated");
      await onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <GateShell icon={KeyRound} title="Set a new password" subtitle="For your security, please choose a new, strong password. It can't match a previous one." onLogout={onLogout}>
      <form onSubmit={submit} className="space-y-3" data-testid="force-password-form">
        <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 15 characters)" data-testid="force-pw-new" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100" />
        <PasswordInput value={c} onChange={(e) => setC(e.target.value)} placeholder="Confirm new password" data-testid="force-pw-confirm" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100" />
        <p className="text-xs text-slate-400">Tip: a long passphrase (several words with spaces) is both strong and easy to remember.</p>
        <Button type="submit" disabled={busy} data-testid="force-pw-submit" className="w-full bg-sky-600 hover:bg-sky-700">{busy ? "Saving…" : "Update password"}</Button>
      </form>
    </GateShell>
  );
}

function ForceMfa({ onDone, onLogout }) {
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    client.post("/auth/mfa/begin").then(({ data }) => setQr(data.qr_data_uri)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);
  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await client.post("/auth/mfa/confirm", { code });
      setCodes(data.backup_codes);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  if (codes) {
    return (
      <GateShell icon={ShieldCheck} title="Save your backup codes" subtitle="Store these somewhere safe — each works once if you lose your device." onLogout={onLogout}>
        <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-slate-50 p-3 font-mono text-sm text-slate-700" data-testid="force-mfa-codes">{codes.map((x) => <span key={x}>{x}</span>)}</div>
        <Button onClick={onDone} data-testid="force-mfa-done" className="mt-4 w-full bg-sky-600 hover:bg-sky-700">Continue to DreamCRM</Button>
      </GateShell>
    );
  }
  return (
    <GateShell icon={ShieldCheck} title="Set up two-factor authentication" subtitle="Your administrator requires 2FA. Scan the QR code with an authenticator app, then enter the 6-digit code." onLogout={onLogout}>
      <form onSubmit={confirm} className="space-y-3" data-testid="force-mfa-form">
        {qr ? <img src={qr} alt="2FA QR code" className="mx-auto h-44 w-44 rounded-lg border border-slate-200" data-testid="force-mfa-qr" /> : <p className="text-sm text-slate-400">Loading QR…</p>}
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="force-mfa-code" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm tracking-widest focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100" />
        <Button type="submit" disabled={busy} data-testid="force-mfa-confirm" className="w-full bg-sky-600 hover:bg-sky-700">{busy ? "Verifying…" : "Confirm & enable"}</Button>
      </form>
    </GateShell>
  );
}

export function ForcedSetup() {
  const { user, logout, refreshMe } = useAuth();
  if (user?.must_change_password) return <ForcePassword onDone={refreshMe} onLogout={logout} />;
  return <ForceMfa onDone={refreshMe} onLogout={logout} />;
}
